import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  Container,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  Stack,
  IconButton,
  Divider,
  Avatar,
  Breadcrumbs,
  Link,
  Tooltip,
  Paper,
  Alert,
  Snackbar,
  CircularProgress
} from "@mui/material";
import {
  ArrowBack,
  Share,
  Message,
  CalendarToday,
  Person,
  Phone,
  Email,
  LocationOn,
  AttachMoney,
  School,
  AccessTime,
  Description,
  PictureAsPdf,
  WhatsApp,
  Facebook,
  ContentCopy,
  Edit,
  Send
} from "@mui/icons-material";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { getCourseImageUrl } from "../../../utils/imageUtils";
import ImageWithSkeleton from '../../../Components/ImageWithSkeleton';
import circleContainer from '../../../assets/images/circle-container.svg';
import Layout from "../../Layout/Layout";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { API_URL, createAxiosInstance } from "../../../api";

const levels = [
  {
    value: "primary-7-11",
    key: "Primary (7/11+)",
  },
  {
    value: "primary-5-11",
    key: "Primary (5-11 yrs)",
  },
  {
    value: "secondary-11-14",
    key: "Secondary (11-14 yrs)",
  },
  {
    value: "gcse-15-16",
    key: "GCSE (15-16 yrs)",
  },
  {
    value: "a-level-5-18",
    key: "A-level (15-18 yrs)",
  },
  {
    value: "degree-adults",
    key: "Degree/Adults",
  },
  {
    value: "Adult / Casual learner",
    key: "Adult / Casual learner",
  },
  {
    value: "other",
    key: "Other",
  },
];

const CourseDetailsV2 = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [sending, setSending] = useState(false);

  // Get user ID from Redux (same as other components)
  const userId = useSelector((state) => state.main.userId);
  const isAuth = useSelector((state) => !!state.main.userId);
  
  console.log("Redux userId:", userId, "type:", typeof userId);
  console.log("isAuth:", isAuth);

  useEffect(() => {
    fetchCourse();
  }, [id]);

  const fetchCourse = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_SERVER}/api/courses/${id}`);
      setCourses(response.data);
    } catch (error) {
      console.error("Error fetching course:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMessageModalOpen = () => {
    setMessageModalOpen(true);
  };

  const handleShareModalOpen = () => {
    setShareModalOpen(true);
  };

  const handleShareModalClose = () => {
    setShareModalOpen(false);
  };

  const handleMessageModalClose = () => {
    setMessageModalOpen(false);
    setMessageContent("");
  };


  const sendMessage = async () => {
    console.log("sendMessage - userId:", userId, "type:", typeof userId);
    console.log("sendMessage - isAuth:", isAuth);
    console.log("sendMessage - messageContent:", messageContent);
    
    if (!messageContent.trim()) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Your message is empty, please input your message.",
        confirmButtonColor: "#712d94",
      });
      return;
    }

    if (!isAuth || !userId) {
      Swal.fire({
        icon: "info",
        title: "Confirm",
        html: `You need to <a href='/signin'>sign in</a> or <a href='/signup?guest'>sign up</a> to send a message.`,
        confirmButtonColor: "#712d94",
        cancelButtonColor: "#000000",
        showCancelButton: true,
        confirmButtonText: "Sign Up",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/signup?guest");
        }
      });
      return;
    }

    setSending(true);

    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `/api/messages/`,
        {
          content: messageContent,
          receiverId: Array.isArray(course.userId) ? course.userId[0] : course.userId,
          senderId: userId,
        }
      );

      // API returns 200 for existing conversations and 201 for new conversations
      if (response.status === 200 || response.status === 201) {
        setMessageContent("");
        toast.success("Message sent successfully!", {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          zIndex: 10001
        });
        setMessageModalOpen(false);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: error?.response?.data?.message || "Message send failed, please try again.",
        confirmButtonColor: "#712d94",
      });
    } finally {
      setSending(false);
    }
  };

  const copyToClipboard = () => {
    const url = `${process.env.REACT_APP_URL}/course-detail/${id}`;
    navigator.clipboard.writeText(url).then(
      () => {
        setSnackBarOpen(true);
      },
      (err) => {
        console.error("Could not copy text: ", err);
      }
    );
  };

  const handleFacebookShare = () => {
    const url = `${process.env.REACT_APP_URL}/course-detail/${id}`;
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      "_blank"
    );
  };

  const handleWhatsAppShare = () => {
    const url = `${process.env.REACT_APP_URL}/course-detail/${id}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(url)}`, "_blank");
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackBarOpen(false);
  };

  const navigateToEdit = () => {
    navigate(`/course/edit/${id}`);
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Typography>Loading course details...</Typography>
          </Box>
        </Container>
      </Layout>
    );
  }

  if (!courses || courses.length === 0) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error">Course not found</Alert>
        </Container>
      </Layout>
    );
  }

  const course = courses[0]; // Get the first course from the array
  const courseImageUrl = getCourseImageUrl(course.image);
  const pdfUrl = course.file ? `${process.env.REACT_APP_SERVER}/${course.file}` : null;

  return (
    <Layout>
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
          <Breadcrumbs sx={{ mb: 4 }}>
          <Link
              component="button"
              onClick={() => navigate(-1)}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              textDecoration: 'none',
              color: 'text.secondary',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              transition: 'background-color 0.2s ease, color 0.2s ease',
              '&:hover': { color: '#712d94', backgroundColor: 'rgba(113, 45, 148, 0.08)' }
            }}
          >
              <ArrowBack sx={{ mr: 1, fontSize: 18 }} />
            Back to Courses
          </Link>
        </Breadcrumbs>

          {/* Course Header Card */}
          <Card sx={{ 
            mb: 3, 
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderRadius: 3
          }}>
          <Grid container>
            {/* Course Image */}
              <Grid item xs={12} md={5}>
                <Box sx={{ position: 'relative', height: { xs: 200, md: 300 } }}>
                  <ImageWithSkeleton
                    src={courseImageUrl}
                alt={course.name}
                    height={'100%'}
                    onErrorSrc={'/assets/course-place1.png'}
                  />
                </Box>
            </Grid>

            {/* Course Info */}
              <Grid item xs={12} md={7}>
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header with title and actions */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h4" component="h1" sx={{ 
                      fontWeight: 700, 
                      color: 'text.primary',
                      fontSize: { xs: '1.5rem', md: '1.75rem' },
                      lineHeight: 1.2
                    }}>
                    {course.name}
                  </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {/* Course Type Chips */}
                      {course.types && course.types.map((type, index) => (
                        <Chip 
                          key={index}
                          icon={type === 'in-person' ? <LocationOn sx={{ fontSize: 14 }} /> : <School sx={{ fontSize: 14 }} />} 
                          label={type.charAt(0).toUpperCase() + type.slice(1)} 
                          size="small"
                          sx={{ 
                            bgcolor: type === 'in-person' ? 'primary.50' : 'secondary.50',
                            color: type === 'in-person' ? 'primary.main' : 'secondary.main',
                            border: `1px solid ${type === 'in-person' ? 'primary.200' : 'secondary.200'}`,
                            fontWeight: 500,
                            fontSize: '0.75rem'
                          }}
                        />
                      ))}
                    <Tooltip title="Share Course">
                        <IconButton 
                          onClick={handleShareModalOpen} 
                          size="small"
                          sx={{ 
                            bgcolor: 'primary.50',
                            '&:hover': { bgcolor: 'primary.100' }
                          }}
                        >
                          <Share sx={{ color: 'primary.main', fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    {userId && (
                      <Tooltip title="Edit Course">
                          <IconButton 
                            onClick={navigateToEdit}
                            size="small"
                            sx={{ 
                              bgcolor: 'secondary.50',
                              '&:hover': { bgcolor: 'secondary.100' }
                            }}
                          >
                            <Edit sx={{ color: 'secondary.main', fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>

                  {/* Course Details Grid */}
                  <Grid container spacing={3} sx={{ flexGrow: 1, mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ 
                          bgcolor: 'primary.50', 
                          p: 1, 
                          borderRadius: 1, 
                          mr: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <School sx={{ color: 'primary.main', fontSize: 20 }} />
                        </Box>
                  <Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            Subject
                    </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {course.subject || 'N/A'}
                        </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ 
                          bgcolor: 'secondary.50', 
                          p: 1, 
                          borderRadius: 1, 
                          mr: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <School sx={{ color: 'secondary.main', fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            Level
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {course.level || 'N/A'}
                        </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ 
                          bgcolor: 'success.50', 
                          p: 1, 
                          borderRadius: 1, 
                          mr: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <CalendarToday sx={{ color: 'success.main', fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            Duration
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {course.duration ? (course.duration === '12' ? '1 Year' : course.duration === '1' ? '1 Month' : `${course.duration} Months`) : 
                              course.isOngoing ? 'Ongoing' : 
                                course.date && course.date.length > 0 ? 
                                  `${new Date(course.date[0]?.split("T")?.[0]).toLocaleDateString("en-GB", {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric"
                                  })} - ${new Date(course.date[1]?.split("T")?.[0]).toLocaleDateString("en-GB", {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric"
                                  })}` : 'N/A'}
                        </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ 
                          bgcolor: 'warning.50', 
                          p: 1, 
                          borderRadius: 1, 
                          mr: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <AccessTime sx={{ color: 'warning.main', fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            Time
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {course.time || 'N/A'}
                        </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Tutor Information */}
                    {course.by && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ 
                            bgcolor: 'info.50', 
                            p: 1, 
                            borderRadius: 1, 
                            mr: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Person sx={{ color: 'info.main', fontSize: 20 }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Tutor
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {course.by}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}

                    {/* Course Price */}
                    {course.coursePrice && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{
                            bgcolor: 'error.50',
                            p: 1,
                            borderRadius: 1,
                            mr: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <AttachMoney sx={{ color: 'error.main', fontSize: 20 }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Price
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {course.coursePrice !== '0' && course.coursePrice !== 0 ? `£${course.coursePrice}` : 'Free'}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}

                    {/* Course Status */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ 
                          bgcolor: course.isApproved ? 'success.50' : 'warning.50', 
                          p: 1, 
                          borderRadius: 1, 
                          mr: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Description sx={{ color: course.isApproved ? 'success.main' : 'warning.main', fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            Status
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {course.isApproved ? 'Approved' : 'Pending Review'}
                        </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Expiration Date */}
                    {course.expirationDate && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ 
                            bgcolor: 'purple.50', 
                            p: 1, 
                            borderRadius: 1, 
                            mr: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <CalendarToday sx={{ color: 'purple.main', fontSize: 20 }} />
                  </Box>
                  <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              Expires
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {new Date(course.expirationDate).toLocaleDateString("en-GB", {
                                day: "numeric",
                                month: "long",
                                year: "numeric"
                              })}
                    </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  {/* Contact Information */}
                  {(course.telephone || course.email || course.socialLink || course.addOne || course.addTwo || course.city || course.town || course.postCode) && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
                      Contact Information
                    </Typography>
                      <Stack spacing={1.5}>
                        {course.telephone && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ 
                              bgcolor: 'info.50', 
                              p: 1, 
                              borderRadius: 1, 
                              mr: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <Phone sx={{ color: 'info.main', fontSize: 18 }} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{course.telephone}</Typography>
                        </Box>
                      )}
                      {course.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ 
                              bgcolor: 'error.50', 
                              p: 1, 
                              borderRadius: 1, 
                              mr: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <Email sx={{ color: 'error.main', fontSize: 18 }} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{course.email}</Typography>
                          </Box>
                        )}
                        {course.socialLink && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ 
                              bgcolor: 'purple.50', 
                              p: 1, 
                              borderRadius: 1, 
                              mr: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <LocationOn sx={{ color: 'purple.main', fontSize: 18 }} />
                            </Box>
                            <Link 
                              href={course.socialLink.startsWith('http') ? course.socialLink : `https://${course.socialLink}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              sx={{ 
                                fontWeight: 500, 
                                fontSize: '0.875rem',
                                color: '#712d94',
                                textDecoration: 'underline',
                                '&:hover': {
                                  textDecoration: 'underline',
                                  color: '#5a2470'
                                }
                              }}
                            >
                              {course.socialLink}
                            </Link>
                        </Box>
                      )}
                        {(course.addOne || course.addTwo || course.city || course.town || course.postCode) && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ 
                              bgcolor: 'grey.100', 
                              p: 1, 
                              borderRadius: 1, 
                              mr: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <LocationOn sx={{ color: 'grey.600', fontSize: 18 }} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {[
                                course.addOne,
                                course.addTwo,
                                course.town,
                                course.city,
                                course.postCode
                              ].filter(value => value && value.trim() !== "")
                              .map(value => value.trim().replace(/,$/, ''))
                              .join(", ")}
                            </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                  )}

                  {/* Contact Information and Price Row */}
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
                      Contact Information
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Grid container spacing={2} sx={{ flex: 1 }}>
                        {course.telephone && (
                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box sx={{ 
                                bgcolor: 'info.50', 
                                p: 0.75, 
                                borderRadius: 1, 
                                mr: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 28,
                                height: 28
                              }}>
                                <Phone sx={{ color: 'info.main', fontSize: 14 }} />
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>{course.telephone}</Typography>
                            </Box>
                          </Grid>
                        )}
                        {course.email && (
                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box sx={{ 
                                bgcolor: 'error.50', 
                                p: 0.75, 
                                borderRadius: 1, 
                                mr: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 28,
                                height: 28
                              }}>
                                <Email sx={{ color: 'error.main', fontSize: 14 }} />
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>{course.email}</Typography>
                            </Box>
                          </Grid>
                        )}
                        {course.socialLink && (
                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box sx={{ 
                                bgcolor: 'purple.50', 
                                p: 0.75, 
                                borderRadius: 1, 
                                mr: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 28,
                                height: 28
                              }}>
                                <LocationOn sx={{ color: 'purple.main', fontSize: 14 }} />
                              </Box>
                              <Link 
                                href={course.socialLink.startsWith('http') ? course.socialLink : `https://${course.socialLink}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                sx={{ 
                                  fontWeight: 500, 
                                  fontSize: '0.85rem',
                                  color: '#712d94',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                    color: '#5a2470'
                                  }
                                }}
                              >
                                {course.socialLink}
                              </Link>
                            </Box>
                          </Grid>
                        )}
                        {(course.addOne || course.addTwo || course.city || course.town || course.postCode) && (
                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box sx={{ 
                                bgcolor: 'grey.100', 
                                p: 0.75, 
                                borderRadius: 1, 
                                mr: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 28,
                                height: 28
                              }}>
                                <LocationOn sx={{ color: 'grey.600', fontSize: 14 }} />
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                                {[
                                  course.addOne,
                                  course.addTwo,
                                  course.town,
                                  course.city,
                                  course.postCode
                                ].filter(value => value && value.trim() !== "")
                                .map(value => value.trim().replace(/,$/, ''))
                                .join(", ")}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                      
                      {/* Price Circle - Positioned at the end of Contact Information */}
                      <Box sx={{ 
                        position: 'relative',
                        width: 60,
                        height: 60,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        ml: 2
                      }}>
                        <img 
                          src={circleContainer} 
                          alt="Price Circle" 
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'contain' 
                          }} 
                        />
                        <Box sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          textAlign: 'center',
                          zIndex: 1
                        }}>
                          <Typography variant="body2" sx={{ 
                            fontWeight: 700, 
                            color: '#712d94',
                            fontSize: '0.8rem',
                            lineHeight: 1,
                            mb: 0.1
                          }}>
                            {course.coursePrice && course.coursePrice !== '0' && course.coursePrice !== 0 ? `£${course.coursePrice}` : 'Free'}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            color: '#712d94',
                            fontSize: '0.5rem',
                            fontWeight: 500,
                            textAlign: 'center',
                            display: 'block'
                          }}>
                            Price
                          </Typography>
                        </Box>
                        {course.coursePrice === '0' && (
                          <Box sx={{
                            position: 'absolute',
                            top: -4,
                            right: -4,
                            bgcolor: 'success.main',
                            borderRadius: '50%',
                            width: 16,
                            height: 16,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                            zIndex: 2
                          }}>
                            <Typography sx={{ 
                              color: 'white', 
                              fontSize: '0.5rem', 
                              fontWeight: 700 
                            }}>
                              FREE
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>

                {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                        startIcon={<Message sx={{ fontSize: 16 }} />}
                    onClick={handleMessageModalOpen}
                        size="medium"
                    sx={{ 
                      flex: 1, 
                          minWidth: 140,
                          py: 1,
                      background: 'linear-gradient(135deg, #712d94 0%, #5a2470 100%)',
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          boxShadow: '0 4px 20px rgba(113, 45, 148, 0.3)',
                      '&:hover': {
                            background: 'linear-gradient(135deg, #5a2470 0%, #4a1f5c 100%)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 6px 25px rgba(113, 45, 148, 0.4)'
                      }
                    }}
                  >
                    Message Tutor
                  </Button>
                      {course.file && course.file !== "" && (
                    <Button
                      variant="outlined"
                          startIcon={<PictureAsPdf sx={{ fontSize: 16 }} />}
                          href={course.file.startsWith('http') ? course.file : `${process.env.REACT_APP_SERVER}/${course.file}`}
                      target="_blank"
                          rel="noopener noreferrer"
                          size="medium"
                          sx={{ 
                            flex: 1, 
                            minWidth: 140,
                            py: 1,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                              borderColor: 'primary.dark',
                              backgroundColor: 'primary.50',
                              transform: 'translateY(-1px)'
                            }
                          }}
                        >
                          {course.file.endsWith(".pdf") ? "View PDF" : "View Document"}
                    </Button>
                  )}
                    </Box>
                </Box>
              </CardContent>
            </Grid>
          </Grid>
        </Card>

        {/* Course Description */}
        {course.description && (
            <Card sx={{ 
              mb: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              borderRadius: 3
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  mb: 2, 
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: '1.1rem'
                }}>
                  <Description sx={{ color: 'primary.main', fontSize: 18 }} />
                About This Course
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                    color: '#1a1a1a',
                    lineHeight: 1.6,
                    fontSize: '0.95rem',
                    color: 'text.secondary',
                    '& p': { mb: 1.5 },
                    '& ul, & ol': { pl: 2.5, mb: 1.5 },
                  '& li': { mb: 0.5 }
                }}
                dangerouslySetInnerHTML={{ __html: course.description }}
              />
            </CardContent>
          </Card>
        )}

        </Container>

        {/* Share Modal */}
        <Modal
          open={shareModalOpen}
          onClose={handleShareModalClose}
          aria-labelledby="share-modal-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            '&:focus': {
              outline: 'none'
            }
          }}
        >
          <Paper
            sx={{
              p: 4,
              maxWidth: 500,
              width: '90%',
              textAlign: 'center',
              borderRadius: 2,
              outline: 'none',
              border: 'none'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Share This Course
            </Typography>
            
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
              <Tooltip title="Share on WhatsApp">
                <IconButton 
                  onClick={handleWhatsAppShare}
                  sx={{ 
                    bgcolor: '#25D366', 
                    color: 'white',
                    '&:hover': { bgcolor: '#128C7E' }
                  }}
                >
                  <WhatsApp />
                </IconButton>
              </Tooltip>
              <Tooltip title="Share on Facebook">
                <IconButton 
                  onClick={handleFacebookShare}
                  sx={{ 
                    bgcolor: '#1877F2', 
                    color: 'white',
                    '&:hover': { bgcolor: '#166FE5' }
                  }}
                >
                  <Facebook />
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy Link">
                <IconButton 
                  onClick={copyToClipboard}
                  sx={{ 
                    bgcolor: 'grey.800', 
                    color: 'white',
                    '&:hover': { bgcolor: 'grey.900' }
                  }}
                >
                  <ContentCopy />
                </IconButton>
              </Tooltip>
            </Stack>

            <TextField
              fullWidth
              value={`${window.location.origin}/course-detail/${id}`}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <Button onClick={copyToClipboard} size="small">
                    Copy
                  </Button>
                )
              }}
              sx={{ mb: 2 }}
            />

            <Button
              variant="contained"
              onClick={handleShareModalClose}
              sx={{ 
                background: 'linear-gradient(135deg, #712d94 0%, #5a2470 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a2470 0%, #712d94 100%)',
                }
              }}
            >
              Close
            </Button>
          </Paper>
        </Modal>

        {/* Message Modal */}
        <Modal
          open={messageModalOpen}
          onClose={handleMessageModalClose}
          aria-labelledby="message-modal-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            '&:focus': {
              outline: 'none'
            },
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }
          }}
        >
          <Paper
            sx={{
              p: 4,
              maxWidth: 600,
              width: '90%',
              borderRadius: 2,
              outline: 'none',
              border: 'none',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              '&:focus': {
                outline: 'none'
              }
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Write Your Message Here
            </Typography>
            
            <TextField
              multiline
              rows={4}
              fullWidth
              placeholder="Type a message"
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#712d94',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#712d94',
                  },
                },
              }}
            />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={handleMessageModalClose}>
                Close
              </Button>
              <Button 
                variant="contained"
                onClick={sendMessage}
                disabled={!messageContent.trim() || sending}
                startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <Send />}
                sx={{ 
                  background: 'linear-gradient(135deg, #712d94 0%, #5a2470 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a2470 0%, #712d94 100%)',
                  },
                  '&:disabled': {
                    background: '#ccc',
                  }
                }}
              >
                {sending ? 'Sending...' : 'Send'}
              </Button>
            </Box>
          </Paper>
        </Modal>

        {/* Snackbar for copy confirmation */}
        <Snackbar
          open={snackBarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          message="Link copied to clipboard!"
        />

      </Box>
    </Layout>
  );
};

export default CourseDetailsV2;
